import { useCallback, useEffect, useMemo, useState } from 'react'

import { TCustomizedMetric, TMetricSettings } from 'types/GlobalMetric'
import { TKeyMetric } from 'types/GlobalKeyMetric'
import { RollingType } from 'types/GlobalKpiOption'
import { ShowPerEnum } from 'types/GlobalMetric'

import { useTranslation } from 'language/translator'
import { useMetric, useMetrics } from 'redux/hooks/Metrics'

import { useAvailableMetrics } from 'components_new/organisms/MetricsTable/utils'

// ─── Shared constants ───────────────────────────────────────────────────────

export const DEFAULT_SETTINGS: TMetricSettings = {
  distribution: null,
  rolling_sum: null,
  slices: [],
  variant_kpi_template_id: null
}

export const EMPTY_METRIC: TCustomizedMetric = {
  id: '',
  settings: DEFAULT_SETTINGS
}

export const EMPTY_KEY_METRIC: TKeyMetric = {
  children: [],
  entity_id: null,
  groups: [],
  id: '',
  increase_is_positive: true,
  index: 0,
  metric: EMPTY_METRIC,
  parent: null,
  pin_comment: null,
  pin_id: null,
  pinned: false,
  resources: [],
  responsible_account_id: null,
  target_activated: false,
  target_value: null,
  title: ''
}

// ─── useMetricEdit ───────────────────────────────────────────────────────────

export function useMetricEdit({
  keyMetric,
  metric
}: {
  keyMetric: TKeyMetric | null | undefined
  metric: TCustomizedMetric | null
}) {
  const [editMetric, setEditMetric] = useState<TCustomizedMetric>(EMPTY_METRIC)
  const [editKeyMetric, setEditKeyMetric] = useState<TKeyMetric | null>(null)

  const updateKeyMetric = useCallback((update: Partial<TKeyMetric>) => {
    setEditKeyMetric((prev) => (prev ? { ...prev, ...update } : prev))
  }, [])

  useEffect(() => {
    if (metric === null) setEditMetric(EMPTY_METRIC)
    if (keyMetric === null) setEditKeyMetric(EMPTY_KEY_METRIC)
    if (metric === null || keyMetric === null) return

    if (keyMetric) {
      setEditKeyMetric(keyMetric)
    }

    setEditMetric({
      ...metric,
      settings: {
        ...DEFAULT_SETTINGS,
        ...(metric.settings ?? {})
      }
    })
  }, [metric?.id, keyMetric])

  const updateSettings = useCallback(
    (update: Partial<TCustomizedMetric['settings']>) => {
      setEditMetric((prev) => ({
        ...prev,
        settings: {
          ...DEFAULT_SETTINGS,
          ...(prev.settings ?? {}),
          ...update
        }
      }))
    },
    []
  )

  const handleVariantChange = useCallback(
    (value: { id: string }) =>
      updateSettings({ variant_kpi_template_id: value.id }),
    [updateSettings]
  )

  const handleDistributionChange = useCallback(
    (value: { id: ShowPerEnum } | null) =>
      updateSettings({ distribution: value?.id ?? null }),
    [updateSettings]
  )

  const handleMetricChange = useCallback((value: { id: string }) => {
    setEditMetric({ id: value.id, settings: DEFAULT_SETTINGS })
  }, [])

  const handleRollingSumChange = useCallback(
    (value: { id: RollingType } | null) =>
      updateSettings({ rolling_sum: value?.id ?? null }),
    [updateSettings]
  )

  const handleSlicesChange = useCallback(
    (value: { id: string }[]) =>
      updateSettings({ slices: value.map((slice) => slice.id) }),
    [updateSettings]
  )

  const reset = useCallback(() => {
    setEditMetric(
      metric
        ? {
            ...metric,
            settings: { ...DEFAULT_SETTINGS, ...(metric.settings ?? {}) }
          }
        : EMPTY_METRIC
    )
    setEditKeyMetric(keyMetric ?? null)
  }, [metric, keyMetric])

  return {
    editKeyMetric,
    editMetric,
    handleDistributionChange,
    handleMetricChange,
    handleRollingSumChange,
    handleSlicesChange,
    handleVariantChange,
    reset,
    updateKeyMetric
  }
}

// ─── useMetricEditOptions ────────────────────────────────────────────────────

export function useMetricEditOptions(editMetric: TCustomizedMetric) {
  const [allMetrics] = useMetrics()
  const metrics = useAvailableMetrics(allMetrics, false, null)
  const { lang, translateEnum } = useTranslation()
  const fullMetric = useMetric(
    editMetric.id,
    editMetric.settings.variant_kpi_template_id
  )

  const metricOptions = useMemo(
    () => metrics.map((m) => ({ id: m.id, title: m.title[lang] })),
    [metrics, lang]
  )

  const selectedMetricOption = useMemo(
    () =>
      fullMetric ? { id: editMetric.id, title: fullMetric.title[lang] } : null,
    [fullMetric, editMetric.id, lang]
  )

  const rollingSumOptions = useMemo(
    () => fullMetric?.rolling_sums.map((rs) => ({ id: rs, title: rs })) ?? [],
    [fullMetric]
  )

  const selectedRollingSumOption = useMemo(() => {
    const rollingSum = editMetric.settings?.rolling_sum

    return rollingSum
      ? {
          id: rollingSum,
          title: translateEnum('rolling_types', rollingSum).abbreviation
        }
      : null
  }, [editMetric.settings?.rolling_sum, translateEnum])

  const distributionOptions = useMemo(
    () =>
      fullMetric?.distributions.map((d) => ({
        id: d.enum,
        title: translateEnum('show_pers', d.enum).abbreviation
      })) ?? [],
    [fullMetric, translateEnum]
  )

  const selectedDistributionOption = useMemo(
    () =>
      distributionOptions.find(
        (o) => o.id === editMetric.settings?.distribution
      ) ?? null,
    [distributionOptions, editMetric.settings?.distribution]
  )

  const sliceOptions = useMemo(
    () =>
      fullMetric
        ? fullMetric.slices.available.map((slice) => ({
          id: slice.id,
          title: slice.name
        }))
        : [],
    [fullMetric]
  )

  const selectedSliceOptions = useMemo(
    () =>
      fullMetric
        ? (editMetric.settings?.slices ?? []).map((sliceId) => ({
            id: sliceId,
            title:
              fullMetric.slices.all.find((s) => s.id === sliceId)?.name ?? ''
          }))
        : [],
    [editMetric.settings?.slices, fullMetric]
  )

  return {
    distributionOptions,
    fullMetric,
    metricOptions,
    rollingSumOptions,
    selectedDistributionOption,
    selectedMetricOption,
    selectedRollingSumOption,
    selectedSliceOptions,
    sliceOptions
  }
}
